import { FeatureFlags } from '@chiroup/core/constants/flags';
import { hasProperty } from '@chiroup/core/functions/hasProperty';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { ErrorResponse } from '@chiroup/core/types/ErrorResponse.type';
import { Me, MeClinic, Plan } from '@chiroup/core/types/Me.type';
import { HasRoleOptions, UserRoles } from '@chiroup/core/types/User.type';
import * as Sentry from '@sentry/react';
import { ValueOf } from '@visx/scale';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ToastContext } from '../contexts/toast.context';
import userService from '../services/user.service';

declare const chmln: any;

const getMeQuery = () => {
  return () => {
    return userService.me();
  };
};

const useMe = (plan?: Plan) => {
  const queryClient = useQueryClient();
  const [meId, setMeId] = useState<string>();
  const [impersonatedFlags, setImpersonatedFlags] = useState<Record<string, boolean>>({});
  const [impersonatedRole, setImpersonatedRole] = useState<UserRoles[] | null>(null);
  const { createToast } = use(ToastContext);
  const [downForMaintenance, setDownForMaintenance] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const navigate = useNavigate();
  const { data: me } = useQuery<Me | undefined, ErrorResponse>(['me'], getMeQuery(), {
    refetchOnWindowFocus: false,
    retry: false,
    onError: err => {
      if (err?.response?.data?.statusCode === 503) {
        setDownForMaintenance(err?.response?.data as any);
      }
    },
  });
  const canImpersonate = me?.showImpersonator && me?.email?.endsWith('@chiroup.com');

  const activeLocations = useMemo(() => {
    if (me?.selectedClinic?.locations?.length) {
      return me?.selectedClinic?.locations?.filter(location => location.active);
    }

    return [];
  }, [me]);

  const clearSelectedClinic = useCallback(
    (user: Sentry.User) => {
      if (user?.ID) {
        try {
          localStorage.setItem(`${user.ID}:selectedClinic`, 'none');
        } catch (err) {
          console.error(err);
        }
      }

      queryClient.setQueryData(['me'], prev => {
        return prev || user
          ? {
              ...(prev || user),
            }
          : undefined;
      });
      setMeId(user?.ID);
      navigate('/');
    },
    [navigate, queryClient]
  );

  useEffect(() => {
    if (!me) {
      return;
    }

    Sentry.setUser({ id: me?.ID, email: me?.email });

    // Add clinic context for filtering
    Sentry.setTag('clinicId', me?.selectedClinic?.ID?.toString() || 'none');
    Sentry.setTag('hasEHR', me?.selectedClinic?.ehr ? 'true' : 'false');
    let lsSelectedClinic: string | null = null,
      lsSelectedLocation: string | null = null;

    try {
      lsSelectedClinic = localStorage.getItem(`${me?.ID}:selectedClinic`);
      lsSelectedLocation = localStorage.getItem(`${me?.ID}:${lsSelectedClinic}:selectedLocation`);
    } catch (err) {
      console.error(err);
    }

    if (lsSelectedClinic === 'none') {
      setMeId(me?.ID);

      return;
    } else if (!lsSelectedClinic) {
      setMeId(me?.ID);

      if (me?.clinics?.length === 1) {
        try {
          localStorage.setItem(`${me?.ID}:selectedClinic`, String(me?.clinics?.[0]?.ID));
        } catch (err) {
          console.error(err);
        }

        queryClient.setQueryData(['me'], {
          ...me,
          supervisor: me.clinics[0].clinicians?.find(c => c.ID === me.ID)?.supervisor,
          encounterSupervision: me.clinics[0].clinicians?.find(c => c.ID === me.ID)
            ?.encounterSupervision,
          billingSupervision: me.clinics[0].clinicians?.find(c => c.ID === me.ID)
            ?.billingSupervision,
          selectedClinic: me.clinics[0],
        });
      }

      const alreadyOnRootOrCreateClinic =
        window.location.pathname === '/' || window.location.pathname === '/create-clinic';

      if (!alreadyOnRootOrCreateClinic) {
        navigate(me?.clinics?.length ? '/' : '/create-clinic');
      }
    } else {
      let selectedClinic: number | null | ListClinic | MeClinic | undefined = lsSelectedClinic
        ? +lsSelectedClinic
        : null;
      const clinics: ListClinic[] = me?.clinics || [];

      if (!selectedClinic && clinics.length === 1) {
        selectedClinic = me?.clinics?.[0];

        try {
          localStorage.setItem(`${me?.ID}:selectedClinic`, String(me?.clinics?.[0]?.ID));
        } catch (err) {
          console.error(err);
        }
      } else if (selectedClinic) {
        // If we already have a selectedClinic with the same ID, preserve it
        // to avoid overwriting optimistically updated data (e.g., clinicians)
        if (me?.selectedClinic && me.selectedClinic.ID === +selectedClinic) {
          selectedClinic = me.selectedClinic;
        } else {
          const selectedClinicObj = clinics.find(
            clinic => selectedClinic && clinic.ID === +selectedClinic
          );

          if (selectedClinicObj) {
            selectedClinic = selectedClinicObj;
          } else {
            selectedClinic = null;
          }
        }
      }

      if (selectedClinic) {
        const clinicObj =
          typeof selectedClinic === 'number'
            ? clinics.find(clinic => selectedClinic && clinic.ID === selectedClinic)
            : selectedClinic;
        const activeLocs = clinicObj?.locations?.filter(loc => loc.active);
        let locationToSelect =
          lsSelectedLocation &&
          activeLocs?.find(location => lsSelectedLocation && location?.ID === +lsSelectedLocation)
            ? Number(lsSelectedLocation)
            : null;

        if (!locationToSelect && typeof selectedClinic !== 'number') {
          const primaryLocationId = selectedClinic?.locations?.find(
            location => location?.primary
          )?.ID;
          locationToSelect = primaryLocationId || null;
        }

        // Only update if the selectedClinic or selectedLocation has actually changed
        const clinicChanged =
          me?.selectedClinic?.ID !==
          (isMeClinic(selectedClinic) ? selectedClinic.ID : selectedClinic);
        const locationChanged = me?.selectedLocation !== locationToSelect;

        if (clinicChanged || locationChanged || !me?.selectedClinic) {
          queryClient.setQueryData(['me'], {
            ...me,
            supervisor: isMeClinic(selectedClinic)
              ? selectedClinic.clinicians?.find(c => c.ID === me.ID)?.supervisor
              : undefined,
            encounterSupervision: isMeClinic(selectedClinic)
              ? selectedClinic.clinicians?.find(c => c.ID === me.ID)?.encounterSupervision
              : undefined,
            billingSupervision: isMeClinic(selectedClinic)
              ? selectedClinic.clinicians?.find(c => c.ID === me.ID)?.billingSupervision
              : undefined,
            selectedClinic,
            selectedLocation: locationToSelect,
          });
        }
        setMeId(me?.ID);

        if (
          typeof selectedClinic !== 'number' &&
          selectedClinic?.subscription?.status !== 'active' &&
          selectedClinic?.subscription?.status !== 'trialing' &&
          selectedClinic?.subscription?.status !== 'past_due'
        ) {
          navigate('/settings/subscription');

          return;
        }
      } else {
        clearSelectedClinic(me as any);
      }
    }

    if (plan) {
      navigate('/settings/subscription', { state: { plan } });

      return;
    }
  }, [clearSelectedClinic, createToast, me, navigate, plan, queryClient]);

  const unselectClinic = useCallback(() => {
    if (meId) {
      try {
        localStorage.setItem(`${meId}:selectedClinic`, 'none');
      } catch (err) {
        console.error(err);
      }
    }

    queryClient.setQueryData(['me'], (prev: unknown) => {
      return prev
        ? {
            ...prev,
            selectedClinic: undefined,
          }
        : undefined;
    });
    navigate('/');
  }, [meId, navigate, queryClient]);

  useEffect(() => {
    if (me?.ID && me?.selectedClinic) {
      // Check if chmln is defined and has the identify method before calling it
      // This prevents ReferenceError when the Chameleon script hasn't loaded yet
      if (typeof chmln !== 'undefined' && typeof chmln.identify === 'function') {
        chmln.identify(me?.ID, {
          email: me.email,
          name: me.name,
          title: me.title,
          degree: me.degree,
          fname: me.fname,
          lname: me.lname,
          phone: me.phone,
          surveyOptOut: me.surveyOptOut,
          company: {
            uid: me.selectedClinic?.ID,
            ...me.selectedClinic,
          },
        });
      }
    }
  }, [me]);

  const updateMe = (key: keyof Me, value: ValueOf<Me>) => {
    queryClient.setQueryData(['me'], (prev: unknown) => {
      return prev
        ? {
            ...prev,
            [key]: value,
          }
        : undefined;
    });
  };

  const selectClinic = useCallback(
    (clinic: MeClinic) => {
      try {
        localStorage.setItem(`${meId}:selectedClinic`, String(clinic?.ID));
      } catch (err) {
        console.error(err);
      }

      const primaryLocationId = clinic?.locations?.find(location => location?.primary)?.ID;

      try {
        localStorage.setItem(
          `${meId}:${me?.selectedClinic?.ID}:selectedLocation`,
          String(primaryLocationId)
        );
      } catch (err) {
        console.error(err);
      }

      queryClient.setQueryData(['me'], (prev: unknown) => {
        return prev
          ? {
              ...prev,
              selectedClinic: clinic,
              selectedLocation: primaryLocationId,
            }
          : undefined;
      });

      if (clinic?.subscription?.status !== 'active') {
        navigate('/settings/subscription');
      }
    },
    [meId, me?.selectedClinic?.ID, navigate, queryClient]
  );

  const selectLocation = useCallback(
    (location: number, forceSwitch: boolean = false) => {
      if (!me?.selectedClinic) return;

      // Only switch if the location is different or if forceSwitch is true
      if (me.selectedLocation !== location || forceSwitch) {
        try {
          localStorage.setItem(
            `${meId}:${me?.selectedClinic?.ID}:selectedLocation`,
            String(location)
          );
        } catch (err) {
          console.error(err);
        }

        queryClient.setQueryData(['me'], (prev: unknown) => {
          return prev
            ? {
                ...prev,
                selectedLocation: location,
              }
            : undefined;
        });
        window.location.reload();
      }
    },
    [meId, me?.selectedClinic, me?.selectedLocation, queryClient]
  );

  const toggleImpersonatedFlag = useCallback(
    (flag: string, value: boolean) => {
      if (!canImpersonate) return;

      setImpersonatedFlags(prev => {
        // check if flag value is same as original; rm if so, don't want to keep duplicated state
        if (value === me?.selectedClinic?.flags?.[flag]) {
          const newstate = { ...prev };
          delete newstate[flag];

          return newstate;
        }

        return {
          ...prev,
          [flag]: value,
        };
      });
    },
    [canImpersonate, me?.selectedClinic?.flags]
  );

  const hasAccess = (flag: string) => {
    // First check if we have an impersonated value for this flag
    if (flag in impersonatedFlags) {
      return impersonatedFlags[flag];
    }

    // Fall back to the actual feature flag value from server
    return me?.selectedClinic?.flags?.[flag] || false;
  };

  const hasEHR = !!me?.selectedClinic?.ehr;
  const hasVoiceToChartAccess = !!me?.selectedClinic?.hasVoiceToChartAccess;
  const hasVoiceToChartSubscription = !!me?.selectedClinic?.hasVoiceToChartSubscription;
  const useCollaborativeEncounters = hasAccess(FeatureFlags.collaborativeEncounter);

  const hasRole = useCallback(
    (roles: UserRoles[], option?: HasRoleOptions) => {
      // If we're impersonating a role, use that instead of the actual role
      const effectiveRoles = impersonatedRole ? impersonatedRole : me?.selectedClinic?.role || [];

      if (option === HasRoleOptions.HasAllRoles) {
        return roles.every(r => effectiveRoles.includes(r));
      }

      if (option === HasRoleOptions.HasSingleRole) {
        return effectiveRoles.length === 1 && effectiveRoles[0] === roles[0];
      }

      return roles.some(r => effectiveRoles.includes(r));
    },
    [impersonatedRole, me?.selectedClinic?.role]
  );

  const setRole = useCallback(
    (role: UserRoles[]) => {
      if (!canImpersonate) return;

      setImpersonatedRole(role);
    },
    [canImpersonate]
  );

  const clearRole = useCallback(() => {
    if (!canImpersonate) return;

    setImpersonatedRole(null);
  }, [canImpersonate]);

  const selectedLocationFull = useMemo(() => {
    const fullSelectedLocation = activeLocations?.find(loc => loc.ID === me?.selectedLocation);
    const primaryLocation = activeLocations?.find(loc => loc.primary);

    return {
      ID: fullSelectedLocation?.ID,
      name: fullSelectedLocation?.name,
      primaryName: primaryLocation?.name,
      address1: fullSelectedLocation?.address1,
      address2: fullSelectedLocation?.address2,
      city: fullSelectedLocation?.city,
      state: fullSelectedLocation?.state,
      country: fullSelectedLocation?.country,
      zip: fullSelectedLocation?.zip,
      phone: fullSelectedLocation?.phone,
      fax: fullSelectedLocation?.fax,
      logo: fullSelectedLocation?.logo || '',
      timezone: fullSelectedLocation?.timezone,
      email: fullSelectedLocation?.email,
      site: fullSelectedLocation?.site,
      primaryLogo: primaryLocation?.logo || '',
      merchantAccounts: fullSelectedLocation?.merchantAccounts,
      paymentProcessors: fullSelectedLocation?.paymentProcessors,
      serviceTaxRate: fullSelectedLocation?.serviceTaxRate,
      productTaxRate: fullSelectedLocation?.productTaxRate,
      allLocations: activeLocations,
      defaultEpayPublicKey: fullSelectedLocation?.merchantAccounts?.[0]?.publicKeys?.[0],
      color: fullSelectedLocation?.color,
    };
  }, [me?.selectedLocation, activeLocations]);

  return {
    me,
    updateMe,
    unselectClinic,
    selectClinic,
    hasAccess,
    hasEHR,
    hasVoiceToChartAccess,
    hasVoiceToChartSubscription,
    useCollaborativeEncounters,
    selectLocation,
    activeLocations,
    selectedLocationFull,
    hasRole,
    downForMaintenance,
    impersonatedFlags,
    toggleImpersonatedFlag,
    impersonatedRole,
    setRole,
    clearRole,
  };
};

export default useMe;

export const isMeClinic = (obj: any): obj is MeClinic => {
  return (
    //Probably is a me clinic
    obj !== null &&
    typeof obj === 'object' &&
    hasProperty(obj, 'ID') &&
    typeof obj.ID === 'number' &&
    hasProperty(obj, 'clinicians') &&
    Array.isArray(obj.clinicians) &&
    hasProperty(obj, 'locations') &&
    Array.isArray(obj.locations)
  );
};
